export type Tier = 'silver' | 'gold' | 'platinum'
export type PartnerStatus = 'active' | 'suspended'
export type ApplicationStatus = 'pending' | 'approved' | 'rejected'
export type EntryType = 'deal_share' | 'signup_bonus' | 'adjustment' | 'payout'
export type EntryStatus = 'pending' | 'claimable' | 'void'
export type PayoutStatus = 'open' | 'fulfilled' | 'cancelled'
export type VolumeSourceKind = 'saleshub' | 'allocation_commit' | 'none'
export type CompensationForm = 'cash' | 'token' | 'equity'

export const TIERS: Tier[] = ['silver', 'gold', 'platinum']

export type Partner = {
  id: number
  privy_id: string
  handle: string
  code: string
  status: PartnerStatus
  tier: Tier
  tier_override?: Tier
  payout_wallet?: string
  payout_chain_id?: number
  notes?: string
  created_at: string
  updated_at: string
}

export type Stats = {
  sign_ups: number
  active_referrals: number
  earned_all_time_usdc: string
  claimable_usdc: string
  pending_usdc: string
  paid_usdc: string
}

export type PartnerListItem = Partner & { stats: Stats }

export type CampaignLink = {
  id: number
  partner_id: number
  code: string
  label: string
  created_at: string
}

export type LedgerEntry = {
  id: number
  partner_id: number
  type: EntryType
  amount_usdc: string
  status: EntryStatus
  deal_id?: number
  contribution_id?: number
  referral_privy_id?: string
  tier_at_accrual?: Tier
  share_bips_at_accrual?: number
  rate_bips_at_accrual?: number
  config_version: number
  claimable_at?: string
  reason?: string
  created_at: string
}

export type PartnerDetail = {
  partner: Partner
  stats: Stats
  // The backend serializes empty lists as Go nil slices, which marshal to
  // JSON null rather than []. Callers must default these to [] before use.
  campaign_links: CampaignLink[] | null
  ledger: LedgerEntry[] | null
}

export type Referral = {
  privy_id: string
  attributed_at: string
  volume_usdc: string
  active: boolean
  campaign_link_label?: string
}

export type Application = {
  id: number
  privy_id: string
  handle: string
  x_handle: string
  telegram_handle: string
  audience_size: number
  note: string
  status: ApplicationStatus
  reviewed_by?: string
  created_at: string
}

export type PayoutRequest = {
  id: number
  partner_id: number
  amount_usdc: string
  status: PayoutStatus
  tx_hash?: string
  fulfilled_by?: string
  created_at: string
  fulfilled_at?: string
}

export type TierConfig = {
  name: Tier
  min_active_referrals: number
  share_bips: number
  signup_bonus_usdc: string
  campaign_link_limit: number
}

export type ProgramConfig = {
  tiers: TierConfig[]
  signup_min_deposit_usdc: string
  signup_bonus_hold_days: number
  attribution_window_hours: number
}

export type ConfigVersion = {
  version: number
  config: ProgramConfig
  created_by: string
  created_at: string
}

export type VolumeSource = {
  deal_id: number
  kind: VolumeSourceKind
  chain_id: number
  contract_address: string
  track_ids: number[]
  token_decimals: number
  start_block: number
  enabled: boolean
  overridden: boolean
  updated_at: string
}

export type DealCompensation = {
  deal_id: number
  form: CompensationForm
  rate_bips: number
  received_at?: string
  updated_by: string
  updated_at: string
}

// Request bodies. Optional fields are omitted from the JSON when undefined,
// which is how the backend's partial PUT preserves untouched values.
export type PartnerWriteBody = {
  privy_id?: string
  handle?: string
  status?: PartnerStatus
  tier_override?: Tier | ''
  payout_wallet?: string
  payout_chain_id?: number
  notes?: string
}

export type AdjustmentBody = {
  amount_usdc: string
  reason: string
}

export type ReviewBody = {
  action: 'approved' | 'rejected'
  payout_wallet?: string
  payout_chain_id?: number
  tier_override?: Tier
}

export type ReviewResponse = {
  application: Application
  partner?: Partner
}

export type VolumeSourceBody = {
  kind: VolumeSourceKind
  chain_id: number
  contract_address: string
  track_ids: number[]
  token_decimals: number
  start_block: number
  enabled: boolean
  overridden: boolean
}

export type CompensationBody = {
  form: CompensationForm
  rate_bips: number
}

export type MarkReceivedResponse = {
  compensation: DealCompensation
  released: number
}

// Shared handle validation: 3-32 letters, digits, underscores or hyphens.
// Mirrors the backend's constraint.
export const HANDLE_RE = /^[A-Za-z0-9_-]{3,32}$/
